import Checkbox from '@jetbrains/ring-ui/components/checkbox/checkbox'
import classNames from 'classnames'
import {Suspense, useContext} from 'react'

import {BuildSelectionIdContext} from '../../../../../contexts/buildSelection'
import {useOneLine} from '../../../../../contexts/oneLine'
import {useBuildId} from '../../../../../hooks/routes'
import {buildSelections} from '../../../../../slices/buildSelections'
import {useIsComparable} from '../../../../pages/CompareBuildsPage/ChooseCompareBuilds/ChooseCompareBuilds.hooks'
import {useHasAvailableActions} from '../../Builds.hooks'

import styles from './BuildCheckbox.css'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

interface Props {
  wrapped?: boolean
}
function BuildCheckbox({wrapped}: Props) {
  const buildId = useBuildId()
  const selectionId = useContext(BuildSelectionIdContext)
  const selection = useAppSelector(state => state.buildSelections[selectionId]) ?? []
  const isOneLine = useOneLine()
  const dispatch = useAppDispatch()
  const isComparable = useIsComparable()
  const hasAvailableActions = useHasAvailableActions(buildId)
  const isSelectable = hasAvailableActions || (selection.length <= 1 && isComparable)
  return buildId != null && selectionId ? (
    <Checkbox
      disabled={!isSelectable && !selection.includes(buildId)}
      data-selection-checkbox-build-id={buildId}
      containerClassName={classNames(styles.container, {
        [styles.wrapped]: wrapped,
        [styles.oneLine]: isOneLine,
      })}
      className={styles.checkbox}
      cellClassName={classNames({
        [styles.hideableCell]: selection.length === 0,
      })}
      aria-label="Select build"
      checked={selection.includes(buildId)}
      onChange={e => {
        const lastChecked = document.querySelector('[data-last-checked]')
        const parent = e.currentTarget.closest('[data-selection-parent]')
        if (
          (e.nativeEvent as MouseEvent | KeyboardEvent).shiftKey &&
          lastChecked != null &&
          parent?.contains(lastChecked)
        ) {
          const checkboxes = [...parent.querySelectorAll('[data-selection-checkbox-build-id]')]
          const lastCheckedIndex = checkboxes.indexOf(lastChecked)
          const myIndex = checkboxes.indexOf(e.currentTarget)
          const range =
            lastCheckedIndex < myIndex
              ? checkboxes.slice(lastCheckedIndex, myIndex + 1)
              : checkboxes.slice(myIndex, lastCheckedIndex + 1)
          const newSelection = new Set(selection)
          range.forEach(checkbox => {
            const checkboxBuildId = Number(
              checkbox.getAttribute('data-selection-checkbox-build-id'),
            )
            newSelection.add(checkboxBuildId)
          })
          dispatch(
            buildSelections.actions.setSelection({selectionId, selection: [...newSelection]}),
          )
        } else {
          dispatch(
            e.currentTarget.checked
              ? buildSelections.actions.select({selectionId, buildId})
              : buildSelections.actions.deselect({selectionId, buildId}),
          )
        }
        lastChecked?.removeAttribute('data-last-checked')
        e.currentTarget.setAttribute('data-last-checked', 'true')
      }}
    />
  ) : null
}

export default function BuildCheckboxContainer(props: Props) {
  return (
    <Suspense>
      <BuildCheckbox {...props} />
    </Suspense>
  )
}
